/**
 * General Attributes Section
 * 
 * Mapping editor for general attributes.
 * Updated with unified design tokens.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  TableBody,
  TableCell,
  Box,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Star as StarIcon } from '@mui/icons-material';
import SearchableDropdown, { DropdownOption } from '../../../../shared/ui/forms/SearchableDropdown';
import TableHeaderField from '../../../../shared/ui/table/TableHeaderField';
import { BodyText } from '../../../../shared/ui/typography';
import {
  useMenuConfiguration,
  useConnectorBindingConfiguration,
  useConfigurationStore,
} from '../../../../shared/stores/useConfigurationStore';
import { useBoltConfiguration } from '../../../../shared/lib/hooks/useConfigurationForms';
import { useRevitParameterOptions } from '../../../../shared/hooks/useRevitParameterOptions';
import {
  filterTextParameterOptions,
  toRevitParameterType,
} from '../../../../shared/lib/revit/parameterOptionTypeFilter';
import { mappingGeneralAttributesTooltips } from '../../../../shared/config/mappingHelpCatalog';
import {
  getScopeEditorDropdownSx,
  InlineHelpIcon,
  MappingEditorHeader,
  MappingRow,
  ScopeEditorTableVariant,
  scopeEditorDensity,
} from '../../../../shared/ui/mapping/ScopeEditorPrimitives';
import {
  getSemanticSurfaceTokens, layoutGap,
} from '../../../../theme/theme';
import { WorkspacePane } from '../../../../shared/ui/workflow';
interface GeneralAttributesSectionProps {
  skeyValue: { value: string };
  objectTypeNameValue: { value: string };
  pipingSpecValue: { value: string };
  systemGroupingValue: { value: string };
  insulationSpecValue: { value: string };
  tracingSpecValue: { value: string };
  weightValue: { value: string };
  insulationThicknessValue: { value: string };
  onSkeyValueChange: (value: string) => void;
  onObjectTypeNameValueChange: (value: string) => void;
  onPipingSpecValueChange: (value: string) => void;
  onSystemGroupingValueChange: (value: string) => void;
  onInsulationSpecValueChange: (value: string) => void;
  onTracingSpecValueChange: (value: string) => void;
  onWeightValueChange: (value: string) => void;
  onInsulationThicknessValueChange: (value: string) => void;
}

const COLUMN_WIDTHS = { pcfAttribute: '35%', revitParam: '65%' };
const GENERAL_ROW_HEIGHT = scopeEditorDensity.denseRowHeight;
const GENERAL_INPUT_HEIGHT = scopeEditorDensity.inputHeight;

const GeneralAttributesSection: React.FC<GeneralAttributesSectionProps> = ({
  skeyValue,
  objectTypeNameValue,
  pipingSpecValue,
  systemGroupingValue,
  insulationSpecValue,
  tracingSpecValue,
  weightValue,
  insulationThicknessValue,
  onSkeyValueChange,
  onObjectTypeNameValueChange,
  onPipingSpecValueChange,
  onSystemGroupingValueChange,
  onInsulationSpecValueChange,
  onTracingSpecValueChange,
  onWeightValueChange,
  onInsulationThicknessValueChange,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const [filters, setFilters] = useState({ pcfAttribute: '', revitParam: '' });
  const handleFilterChange = (field: 'pcfAttribute' | 'revitParam', value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };
  const clearFilters = () => setFilters({ pcfAttribute: '', revitParam: '' });
  const hasActiveFilters = filters.pcfAttribute !== '' || filters.revitParam !== '';

  const menuConfig = useMenuConfiguration();
  const connectorConfig = useConnectorBindingConfiguration();
  const { options: revitParamOptions } = useRevitParameterOptions({
    preferredParameterType: toRevitParameterType(connectorConfig.parameterBindingKind),
  });
  const syncBoltAttributesToMapping = useConfigurationStore((state) => state.syncBoltAttributesToMapping);
  const { config: boltConfig, updateField: updateBoltField } = useBoltConfiguration();

  const boltBaseFamilyOptions: DropdownOption[] = useMemo(
    () => [
      { value: 'BoltBased', label: 'Use existing BOLT elements', tooltip: 'Use BOLT elements already present in the pipeline; BoltSetAdder does not derive additional BOLT elements.' },
      { value: 'GasketBased', label: 'Derive BOLT elements from GASKET', tooltip: 'BoltSetAdder can derive standalone BOLT elements from qualifying GASKET elements selected by BOLT-SET-ACTIVE.' },
    ],
    []
  );

  const handleBoltBaseFamilyChange = useCallback(
    (value: string) => {
      updateBoltField('boltBaseFamilyValue', value);
      if (value === 'BoltBased') updateBoltField('boltModelingChoice', 'yes');
      else updateBoltField('boltModelingChoice', 'no');
      syncBoltAttributesToMapping();
    },
    [syncBoltAttributesToMapping, updateBoltField]
  );

  const previousPagesOptions: DropdownOption[] = [
    {
      value: menuConfig.objectTypeValue || '',
      label: 'OBJECT-TYPE (Menu)',
      tooltip: 'Use the Revit parameter selected in menu settings.',
      group: 'Menu settings',
    },
    {
      value: menuConfig.sKeyValue || '',
      label: 'SKEY (Menu)',
      tooltip: 'Use the Revit parameter selected in menu settings.',
      group: 'Menu settings',
    },
    {
      value: menuConfig.pipingSpecValue || '',
      label: 'PIPING-SPEC (Menu)',
      tooltip: 'Use the Revit parameter selected in menu settings.',
      group: 'Menu settings',
    },
    {
      value: menuConfig.systemGroupingValue || '',
      label: 'SYSTEM-GROUPING (Menu)',
      tooltip: 'Use the Revit parameter selected in menu settings.',
      group: 'Menu settings',
    },
    {
      value: connectorConfig.insulationSpecValue?.value || '',
      label: 'INSULATION-SPEC (Connector)',
      tooltip: 'Use the Revit parameter selected in connector binding.',
      group: 'Connector binding',
    },
    {
      value: connectorConfig.tracingSpecValue?.value || '',
      label: 'TRACING-SPEC (Connector)',
      tooltip: 'Use the Revit parameter selected in connector binding.',
      group: 'Connector binding',
    },
    {
      value: connectorConfig.insulationThicknessValue?.value || '',
      label: 'INSULATION-THICKNESS (Connector)',
      tooltip: 'Use the Revit parameter selected in connector binding.',
      group: 'Connector binding',
    },
    {
      value: connectorConfig.materialValue?.value || '',
      label: 'MATERIAL (Connector)',
      tooltip: 'Use the Revit parameter selected in connector binding.',
      group: 'Connector binding',
    },
    {
      value: connectorConfig.designStdValue?.value || '',
      label: 'DESIGN-STD (Connector)',
      tooltip: 'Use the Revit parameter selected in connector binding.',
      group: 'Connector binding',
    },
    {
      value: connectorConfig.pressureClassValue?.value || '',
      label: 'PRESSURE-CLASS (Connector)',
      tooltip: 'Use the Revit parameter selected in connector binding.',
      group: 'Connector binding',
    },
  ].filter((opt) => opt.value);

  // General attribute values are written as text; only text-typed Revit parameters
  // are offered (unknown-typed options stay visible — parameterOptionTypeFilter).
  const textRevitParamOptions = useMemo(
    () => filterTextParameterOptions(revitParamOptions),
    [revitParamOptions]
  );
  const allOptions: DropdownOption[] = [...previousPagesOptions, ...textRevitParamOptions];

  const generalDropdownSx = useMemo<SxProps<Theme>>(
    () => getScopeEditorDropdownSx(theme, GENERAL_INPUT_HEIGHT),
    [theme]
  );

  const attributes = useMemo(
    () => [
      {
        id: 'objectType',
        label: 'OBJECT-TYPE',
        displayLabel: 'OBJECT-TYPE',
        value: objectTypeNameValue,
        onChange: onObjectTypeNameValueChange,
        help: mappingGeneralAttributesTooltips.objectType,
        isCrucial: true,
      },
      {
        id: 'skey',
        label: 'SKEY',
        displayLabel: 'SKEY',
        value: skeyValue,
        onChange: onSkeyValueChange,
        help: mappingGeneralAttributesTooltips.sKey,
        isCrucial: true,
      },
      {
        id: 'pipingSpec',
        label: 'PIPING-SPEC',
        displayLabel: 'PIPING-SPEC',
        value: pipingSpecValue,
        onChange: onPipingSpecValueChange,
        help: mappingGeneralAttributesTooltips.pipingSpec,
        isCrucial: true,
      },
      {
        id: 'systemGrouping',
        label: 'SYSTEM-GROUPING',
        displayLabel: 'SYSTEM-GROUPING',
        value: systemGroupingValue,
        onChange: onSystemGroupingValueChange,
        help: mappingGeneralAttributesTooltips.systemGrouping,
        isCrucial: false,
      },
      {
        id: 'insulationSpec',
        label: 'INSULATION-SPEC',
        displayLabel: 'INSULATION-SPEC',
        value: insulationSpecValue,
        onChange: onInsulationSpecValueChange,
        help: mappingGeneralAttributesTooltips.insulationSpec,
        isCrucial: false,
      },
      {
        id: 'tracingSpec',
        label: 'TRACING-SPEC',
        displayLabel: 'TRACING-SPEC',
        value: tracingSpecValue,
        onChange: onTracingSpecValueChange,
        help: mappingGeneralAttributesTooltips.tracingSpec,
        isCrucial: false,
      },
      {
        id: 'weight',
        label: 'WEIGHT',
        displayLabel: 'WEIGHT',
        value: weightValue,
        onChange: onWeightValueChange,
        help: mappingGeneralAttributesTooltips.weight,
        isCrucial: false,
      },
      {
        id: 'insulationThickness',
        label: 'INSULATIONTHICKNESS',
        displayLabel: 'INSULATION-THICKNESS',
        value: insulationThicknessValue,
        onChange: onInsulationThicknessValueChange,
        help: mappingGeneralAttributesTooltips.insulationThickness,
        isCrucial: false,
      },
      {
        id: 'boltBaseFamily',
        label: 'BoltBaseFamily',
        displayLabel: 'Bolt base family',
        value: { value: boltConfig.boltBaseFamilyValue.value },
        onChange: handleBoltBaseFamilyChange,
        help: mappingGeneralAttributesTooltips.boltBaseFamily,
        isCrucial: false,
        customOptions: boltBaseFamilyOptions,
      },
    ],
    [
      objectTypeNameValue,
      onObjectTypeNameValueChange,
      skeyValue,
      onSkeyValueChange,
      pipingSpecValue,
      onPipingSpecValueChange,
      systemGroupingValue,
      onSystemGroupingValueChange,
      insulationSpecValue,
      onInsulationSpecValueChange,
      tracingSpecValue,
      onTracingSpecValueChange,
      weightValue,
      onWeightValueChange,
      insulationThicknessValue,
      onInsulationThicknessValueChange,
      boltConfig.boltBaseFamilyValue.value,
      boltBaseFamilyOptions,
      handleBoltBaseFamilyChange,
    ]
  );

  const filteredAttributes = useMemo(() => {
    return attributes.filter((attr) => {
      const labelSearchText = `${attr.label} ${attr.displayLabel ?? ''}`.toLowerCase();
      const matchesPcf = labelSearchText.includes(filters.pcfAttribute.toLowerCase());
      const matchesRevit = attr.value.value.toLowerCase().includes(filters.revitParam.toLowerCase());
      return matchesPcf && matchesRevit;
    });
  }, [attributes, filters]);

  return (
    <WorkspacePane>
      <ScopeEditorTableVariant ariaLabel="Shared PCF attribute source editor" minWidth={420} rowHeight={GENERAL_ROW_HEIGHT}>
        <MappingEditorHeader
          columns={[
            {
              key: 'pcfAttribute',
              width: COLUMN_WIDTHS.pcfAttribute,
              content: (
                <TableHeaderField
                  label="PCF attribute target"
                  value={filters.pcfAttribute}
                  onChange={(value) => handleFilterChange('pcfAttribute', value)}
                  ariaLabel="Filter PCF attribute targets"
                  inputHeight={GENERAL_INPUT_HEIGHT}
                />
              ),
            },
            {
              key: 'revitParam',
              width: COLUMN_WIDTHS.revitParam,
              content: (
                <TableHeaderField
                  label="Revit source parameter"
                  value={filters.revitParam}
                  onChange={(value) => handleFilterChange('revitParam', value)}
                  ariaLabel="Filter Revit source parameters"
                  inputHeight={GENERAL_INPUT_HEIGHT}
                />
              ),
            },
          ]}
        />
        <TableBody>
          {filteredAttributes.length === 0 ? (
            <MappingRow>
              <TableCell colSpan={2} align="center" sx={{ color: colors.text.secondary, py: 1.5 }}>
                <BodyText secondary color="text.secondary">
                  No shared PCF attribute mappings match the current filters.
                </BodyText>
                {hasActiveFilters && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={clearFilters}
                    sx={{ mt: 0.5, fontSize: '0.6875rem', py: 0.125 }}
                  >
                    Clear filters
                  </Button>
                )}
              </TableCell>
            </MappingRow>
          ) : (
            filteredAttributes.map((attr) => (
              <MappingRow key={attr.id}>
                <TableCell sx={{ width: COLUMN_WIDTHS.pcfAttribute, pr: 0.25 }}>
                  {/* Left rail matches the dropdown input padding (14px) so the
                      label text aligns with the Revit source parameter column. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.micro, minWidth: 0, pl: '14px' }}>
                    {attr.isCrucial && (
                      <StarIcon
                        sx={{
                          fontSize: '0.625rem',
                          color: attr.value.value.trim()
                            ? alpha(theme.palette.warning.main, 0.5)
                            : theme.palette.warning.main,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: layoutGap.micro,
                        minWidth: 0,
                        maxWidth: '100%',
                      }}
                    >
                      <BodyText
                        secondary
                        sx={{
                          minWidth: 0,
                          flex: '0 1 auto',
                          color: colors.text.secondary,
                        }}
                        noWrap
                        title={attr.displayLabel}
                      >
                        {attr.displayLabel}
                      </BodyText>
                      <InlineHelpIcon label={attr.displayLabel} help={attr.help} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.revitParam, pl: 0.25 }}>
                  {'customOptions' in attr && attr.customOptions ? (
                    <SearchableDropdown
                      value={attr.value.value}
                      onChange={attr.onChange}
                      options={attr.customOptions as DropdownOption[]}
                      placeholder="Select export behavior…"
                      freeSolo={false}
                      ariaLabel={`${attr.displayLabel} export behavior`}
                      sx={generalDropdownSx}
                    />
                  ) : (
                    <SearchableDropdown
                      value={attr.value.value}
                      onChange={attr.onChange}
                      options={allOptions}
                      placeholder="Select or enter Revit source parameter…"
                      freeSolo
                      ariaLabel={`Revit source parameter for ${attr.displayLabel}`}
                      sx={generalDropdownSx}
                    />
                  )}
                </TableCell>
              </MappingRow>
            ))
          )}
        </TableBody>
      </ScopeEditorTableVariant>
    </WorkspacePane>
  );
};

export default React.memo(GeneralAttributesSection);
